import React, { Fragment, useState } from 'react';
import { FormattedAlertNote, FormattedAlertReasons } from '../common/helper';
import { Button, Dropdown, Table as ReactTable } from 'react-bootstrap';
import { SalvModal } from '@salvapp/ui-components';
import { MonitoringAlertHistory } from '../alerts/AlertHistory';
import NotesModal, { NotesModalFormValues } from './NotesModal';
import { AppState } from '../store/store';
import { fetchAlerts, saveNote } from './amlActions';
import { connect, ResolveThunks } from 'react-redux';
import AlertRowStatus from '../alerts/AlertRowStatus';
import AlertRowAssignment from '../alerts/AlertRowAssignment';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import { CAN_ADD_NOTE } from '../auth/permissions';
import Has from '../auth/Has';
import { DropdownWrapper } from '../common/DropdownWrapper';
import AlertDetailsPopover from '../alerts/AlertDetailsPopover';
import { InternalApiAlertType, InternalApiMonitoringAlert } from '../api-internal';
import { ContentPreviewer } from '../common/ContentPreviewer';

interface AlertModalProps {
  alert: InternalApiMonitoringAlert;
  personId: string;
}

export const AlertModal = ({ alert, personId, saveNote, fetchAlerts, page, size }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const attributes = ['Date', 'Details', 'Assignee', 'Status', 'Actions'];

  const onSubmitNote = async (values: NotesModalFormValues) => {
    saveNote(personId, values).then(() => {
      fetchAlerts(personId, page, size);
    });
  };
  return (
    <Fragment>
      <Button className='inline-button-details mb-2' variant='link' onClick={() => setShowModal(true)}>
        <FormattedAlertReasons alert={alert} />
      </Button>
      <SalvModal show={showModal} onHide={() => setShowModal(false)}>
        <SalvModal.Header closeButton>Alert</SalvModal.Header>
        <SalvModal.Body>
          <ReactTable striped hover size='sm' className='card-table'>
            <thead>
              <tr>
                {attributes.map((a) => (
                  <th key={a}>
                    <div className='attribute-header'>{a}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className='custom-table-row-0'>
                <td>
                  <FormattedDateTime date={alert.createdTime} showTime />
                </td>
                <td>
                  <AlertDetailsPopover alert={alert} />
                </td>
                <td>
                  <AlertRowAssignment alert={alert} />
                </td>
                <td>
                  <AlertRowStatus
                    alert={alert}
                    alertType={InternalApiAlertType.MONITORING}
                    onChange={() => fetchAlerts(personId, page, size)}
                  />
                </td>
                <td>
                  <DropdownWrapper>
                    <Dropdown.Toggle as={Button} size='sm' className='btn-white' id='actions-dropdown'>
                      •••
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <MonitoringAlertHistory alert={alert} dropdownItem />
                      <Has allPermissions={CAN_ADD_NOTE}>
                        <NotesModal onSubmit={onSubmitNote} alert={alert} dropdownItem />
                      </Has>
                    </Dropdown.Menu>
                  </DropdownWrapper>
                </td>
              </tr>
              {alert.notes &&
                alert.notes.length > 0 &&
                alert.notes
                  .slice()
                  .sort((a, b) => {
                    if (a.createdTime && b.createdTime) {
                      return b.createdTime.getTime() - a.createdTime.getTime();
                    }
                    return 0;
                  })
                  .map((note, noteIndex) => (
                    <tr key={noteIndex} className={`custom-table-row-0`}>
                      <td colSpan={5} className='alert-note-td'>
                        <div className='alert-note'>
                          <ContentPreviewer>
                            <FormattedAlertNote apiNote={note} />
                            {note.content}
                          </ContentPreviewer>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </ReactTable>
        </SalvModal.Body>
      </SalvModal>
    </Fragment>
  );
};

const mapStateToProps = ({ aml }: AppState) => {
  return {
    page: aml.alerts.page,
    size: aml.alerts.size,
  };
};

const mapDispatchToProps = {
  fetchAlerts: fetchAlerts,
  saveNote,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & AlertModalProps;

export default connect(mapStateToProps, mapDispatchToProps)(AlertModal);
